import { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material'

export default function Login({ onLoginSuccess }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('')
  const [forgotPasswordError, setForgotPasswordError] = useState('')
  const [isSendingReset, setIsSendingReset] = useState(false)

  // Get branding from config
  const branding = window.INSIGHT_CONFIG?.BRANDING || {}
  const appName = window.INSIGHT_CONFIG?.APP_NAME || 'Insight'
  const tagline = branding.tagline || 'AI assistant'

  // Check if already logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('cme_token')
    const userStr = localStorage.getItem('cme_user')
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr)
        if (user.role === 'super_admin') {
          window.location.href = 'super-admin.html'
        } else {
          onLoginSuccess()
        }
      } catch (e) {
        // If parsing fails, proceed with login
      }
    }
  }, [onLoginSuccess])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || data.error || 'Login failed')
      }

      // Save token to localStorage
      localStorage.setItem('cme_token', data.access_token)

      // Redirect super_admin immediately (they don't need full profile here)
      if (data.user.role === 'super_admin') {
        localStorage.setItem('cme_user', JSON.stringify(data.user))
        window.location.href = 'super-admin.html'
        return
      }

      // Fetch full user profile (includes theme, last_selected_gpt, etc.)
      try {
        const meResponse = await fetch('/users/me', {
          headers: { Authorization: `Bearer ${data.access_token}` }
        })
        if (meResponse.ok) {
          const fullUser = await meResponse.json()
          localStorage.setItem('cme_user', JSON.stringify(fullUser))
        } else {
          // Fallback to basic user data from login response
          localStorage.setItem('cme_user', JSON.stringify(data.user))
        }
      } catch {
        // Fallback to basic user data from login response
        localStorage.setItem('cme_user', JSON.stringify(data.user))
      }

      onLoginSuccess()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setForgotPasswordError('')
    setForgotPasswordMessage('')
    setIsSendingReset(true)

    try {
      const response = await fetch('/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset email')
      }

      setForgotPasswordMessage(data.message)
    } catch (err) {
      setForgotPasswordError(err.message)
    } finally {
      setIsSendingReset(false)
    }
  }

  const handleBackToLogin = () => {
    setShowForgotPassword(false)
    setForgotPasswordEmail('')
    setForgotPasswordMessage('')
    setForgotPasswordError('')
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400, borderRadius: 4, boxShadow: 6 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" fontWeight={700} color="text.primary" gutterBottom>
              {appName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {tagline}
            </Typography>
          </Box>

          {showForgotPassword ? (
            /* Forgot Password Form */
            <Box component="form" onSubmit={handleForgotPassword}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>

              <TextField
                fullWidth
                label="Email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                margin="normal"
                required
                autoFocus
                placeholder="Enter your email"
              />

              {forgotPasswordError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {forgotPasswordError}
                </Alert>
              )}

              {forgotPasswordMessage && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {forgotPasswordMessage}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSendingReset || !!forgotPasswordMessage}
                sx={{ mt: 3, py: 1.5, fontWeight: 600 }}
              >
                {isSendingReset ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleBackToLogin}
                  sx={{ cursor: 'pointer' }}
                >
                  Back to Login
                </Link>
              </Box>
            </Box>
          ) : (
            /* Login Form */
            <>
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                  placeholder="Enter your email"
                />

                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  placeholder="Enter your password"
                />

                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box sx={{ mt: 1, textAlign: 'right' }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => setShowForgotPassword(true)}
                    sx={{ cursor: 'pointer' }}
                  >
                    Forgot password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading}
                  sx={{ mt: 2, py: 1.5, fontWeight: 600 }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Login'
                  )}
                </Button>
              </Box>

              {/* Info about invitations */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  New to {appName}? Contact your company administrator for an invitation.
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
